<template>
  <div>
    <h3 v-if="loading" data-testid="documents-data-provider-loading">Loading...</h3>
    <RouterView else-v-if="booth || event || partnerCompany" :booth="booth" :event="event" :partner-company="partnerCompany" />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { watch, computed } from 'vue'
import { useRouter } from 'vue-router'

import { useBoothQuery, useEventQuery, usePartnerCompanyByIdQuery } from '@/gql/myomr'
import { toTypeAndId } from '@/helpers/globalIdHelper'
import { globalizeUrlifiedId } from '@/helpers/urlifyGlobalId'
import { useMeStore } from '@/stores'

const router = useRouter()

const props = defineProps<{
  signableId: string
}>()

const meStore = useMeStore()
const { currentPartnerCompanyGlobalId } = storeToRefs(meStore)

const globalId = computed(() => globalizeUrlifiedId(props.signableId))
const detectedType = computed(() => toTypeAndId(globalId.value).type)
const isPartnerCompanyType = computed(() => detectedType.value === 'PartnerCompany')

const {
  result: boothResult,
  loading: boothLoading,
  error: boothError,
} = useBoothQuery(
  () => ({ boothId: globalId.value }),
  () => ({ enabled: !isPartnerCompanyType.value }),
)

const booth = computed(() => {
  const node = boothResult.value?.node
  return node?.__typename === 'Booth' ? node : undefined
})
const isNodeResolved = computed(() => !boothLoading.value && (!!boothResult.value || !!boothError.value))
const isEventLookup = computed(() => !isPartnerCompanyType.value && isNodeResolved.value && !booth.value)

const {
  result: eventResult,
  loading: eventLoading,
  error: eventError,
} = useEventQuery(
  () => ({ id: globalId.value }),
  () => ({ enabled: isEventLookup.value }),
)
const isEventPending = computed(() => isEventLookup.value && !eventResult.value && !eventError.value)

const { result: partnerCompanyResult, loading: partnerCompanyLoading } = usePartnerCompanyByIdQuery(
  () => ({ id: globalId.value }),
  () => ({ enabled: isPartnerCompanyType.value }),
)

const event = computed(() => (isEventLookup.value ? eventResult.value?.eventById : undefined))
const partnerCompany = computed(() => {
  if (!isPartnerCompanyType.value) return undefined
  const node = partnerCompanyResult.value?.node
  return node?.__typename === 'PartnerCompany' ? node : undefined
})
const loading = computed(() => boothLoading.value || eventLoading.value || isEventPending.value || partnerCompanyLoading.value)
function comparePartnerCompanies() {
  if (!booth.value || !currentPartnerCompanyGlobalId.value) return
  if (currentPartnerCompanyGlobalId.value !== booth.value?.partnerCompany?.id) {
    router.push({ name: 'documents' })
  }
}

watch([booth, currentPartnerCompanyGlobalId], () => comparePartnerCompanies())

watch(loading, (isLoading) => {
  if (!isLoading && !booth.value && !event.value && !partnerCompany.value) {
    router.replace({ name: '404' })
  }
})
</script>
